import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import 'pages/AdminClient/ClientAdminUsersManagement.css';
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from 'xlsx';

import { removeWWW, lower } from 'utils/StringUtils';
import { ROLES } from 'utils/Constants';
import { handleSortingChange } from 'helpers/SortingHelper';
import RoleTypeFilter from 'components/shared/Filter/RoleTypeFilter';
import { Paginator } from 'components/shared/Paginator';

import { DeleteUser } from 'components/Admin/DeleteUser';
import { DisableUser } from 'components/AdminClient/DisableUserGlobal';

import { CreateUpdateUser } from 'components/Admin/CreateUpdateUser';
import {
    CustomDropdownReadOnly,
    CustomDropdownWithSelect,
} from 'components/shared/DropDown/CustomDropdown';
import { Loader } from 'components/shared/Loader';

import ErrorComponent from 'components/shared/ErrorComponent';
import { Button } from '@mui/material';
import SearchIcon from 'icons/SearchIcon';
import { ChevronDownIcon, ChevronUpIcon } from 'icons/ChevronIcon';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { projectManagerUserManagementSelector } from 'store/ProjectManager/ProjectManagerManagement';
import { fetchProjectManagerWriters } from 'store/ProjectManager/ProjectManagerManagement/features';
import { DeleteUserGlobal } from 'components/AdminClient/DeleteUser';
import TablePagination from 'components/shared/Pagination/TablePagination';
import { usePagination } from 'utils/hooks/usePagination';
import usePageTracking from 'utils/hooks/usePageTracking';

const PROJECT_MANAGER_USER_MANAGEMENT_PAGE = '/ProjectManager/UserManagement';
const pageNbOptions = [15, 20, 30];
const ITEMS_PER_PAGE = 12;

export default function ClientAdminUsersManagement() {
    usePageTracking(PROJECT_MANAGER_USER_MANAGEMENT_PAGE);

    let { userList, isLoading, isError } = useSelector(
        projectManagerUserManagementSelector
    );
    const { t } = useTranslation();
    const columns = [
        { label: t('Nom'), field: 'name', sortable: true, sort: 'asc' },
        { label: t('Email'), field: 'mail', sortable: true, sort: 'asc' },
        { label: t('Role'), field: 'role', sortable: false, sort: 'asc' },
        /*{
          label: t('Analyse/Limite'),
          field: 'analysis',
          sortable: true,
          sort: 'asc',
        },*/
        { label: t('Actions'), field: 'delete', sortable: false },
    ];

    const createUserMenuList = [
        { text: t('Ajouter un rédacteur'), role: ROLES.GLOBAL_WRITER },
    ];

    const filterRoleMenuList = [
        { type: '', text: t('Tous les rôles') },         // ← change "role" to "type"
        { type: ROLES.GLOBAL_WRITER, text: t('Rédacteur') },
    ];
    const dispatch = useDispatch();

    const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
    const [selectedUserForDisable, setSelectedUserForDisable] = useState(null);
    const [anchorElRole, setAnchorElRole] = useState(null);
    const [selectedUserForCreateUpdate, setSelectedUserForCreateUpdate] =
        useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState(filterRoleMenuList[0]);
    const [anchorEl, setAnchorEl] = useState(null);

    const [usersPerPage, setUsersPerPage] = useState(12);
    const [pageNumbers, setPageNumbers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    // const [currentUsers, setCurrentUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');

    const [showDelete, setShowDelete] = useState(false);
    const [showDisable, setShowDisable] = useState(false);

    const [showCreateUpdate, setShowCreateUpdate] = useState(false);
    const handleRoleFilterClick = (idx, e) => setAnchorElRole({ [idx]: e.currentTarget });
    const handleRoleFilterClose = () => setAnchorElRole(null);
    useEffect(() => {
        window.dispatchEvent(new Event('locationchange'));
        document.title = t('Typix - Gestion des utilisateurs');
        dispatch(fetchProjectManagerWriters());
    }, [dispatch, t]);

    const formatRole = useCallback(
        (role) => {
            if (role === ROLES.WRITER || role === ROLES.GLOBAL_WRITER) {
                return t('Rédacteur');
            } else if (role === ROLES.ADMIN_CLIENT) {
                return t('Administrateur');
            } else if (role === ROLES.PROJECT_MANAGER) {
                return t('Chef de projet');
            } else {
                return lower(role);
            }
        },
        [t]
    );

    useEffect(() => {
        let array = [...userList];

        if (selectedRole.type?.length > 0) {
            array = array.filter((x) => {
                return lower(x.role ?? '').includes(lower(selectedRole.type));
            });
        }

        if (searchValue && searchValue.length > 0) {
            let lowerCaseValue = lower(searchValue);
            array = array.filter((x) => {
                return (
                    x.name?.toLowerCase().includes(lowerCaseValue) ||
                    x.firstName?.toLowerCase().includes(lowerCaseValue) ||
                    x.lastName?.toLowerCase().includes(lowerCaseValue) ||
                    lower(x.mail).includes(lowerCaseValue) ||
                    formatRole(x.role).includes(lowerCaseValue) ||
                    (x.websitesUrls?.length > 0 &&
                        lower(x.websitesUrls[0]).includes(lowerCaseValue))
                );
            });
        }

        setFilteredUsers([...array]);
        setCurrentPage(1);
    }, [selectedRole, searchValue, userList, formatRole]);
    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
    const currentUsers = filteredUsers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );


    const handleSorting = (sortField, sortOrder) => {
        if (sortField) {
            setCurrentPage(1);
            const sorted = [...filteredUsers].sort((a, b) => {

                const getDisplayName = (user) => {
                    if (
                        user.role === ROLES.ADMIN ||
                        user.role === ROLES.ADMIN_CLIENT ||
                        user.role === ROLES.GLOBAL_WRITER ||
                        user.role === ROLES.PROJECT_MANAGER
                    ) {
                        return (user.firstName || '') + ' ' + (user.lastName || '');
                    }
                    return user.name || '';
                };

                if (sortField === 'name') {
                    return (
                        getDisplayName(a).localeCompare(getDisplayName(b), 'en', {
                            numeric: true,
                        }) * (sortOrder === 'asc' ? 1 : -1)
                    );
                }

                if (a[sortField] === undefined || a[sortField] === null) return 1;
                if (b[sortField] === undefined || b[sortField] === null) return -1;

                if (sortField === 'websitesUrls') {
                    if (a[sortField].length === 0) return 1;
                    if (b[sortField].length === 0) return -1;

                    return (
                        a[sortField][0]
                            .toString()
                            .localeCompare(b[sortField][0].toString(), 'en', {
                                numeric: true,
                            }) * (sortOrder === 'asc' ? 1 : -1)
                    );
                }

                if (sortField === 'analysis') {
                    return (
                        (a['limit'] - a['analysis'] - (b['limit'] - b['analysis'])) *
                        (sortOrder === 'asc' ? 1 : -1)
                    );
                }

                return (
                    a[sortField].toString().localeCompare(b[sortField].toString(), 'en', {
                        numeric: true,
                    }) * (sortOrder === 'asc' ? 1 : -1)
                );
            });

            setFilteredUsers(sorted);
        }
    };

    const handleClick = (index, event) => {
        setAnchorEl({ [index]: event.currentTarget });
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleUsersExport = () => {
        const workBook = xlsxUtils.book_new();

        const exportData = filteredUsers.map((user) => ({
            [t('Prénom/Nom')]:
                user.role === ROLES.ADMIN ||
                    user.role === ROLES.ADMIN_CLIENT ||
                    user.role === ROLES.PROJECT_MANAGER ||
                    user.role === ROLES.GLOBAL_WRITER
                    ? (user.firstName || '') + ' ' + (user.lastName || '')
                    : user.name || '',
            [t('Mail')]: user.mail,
            [t('Rôle')]: formatRole(user.role),
            [t('Status')]: user.active ? t('Activé') : t('Déactivé'),
        }));

        const xlsxSheet = xlsxUtils.json_to_sheet(exportData);
        xlsxUtils.book_append_sheet(workBook, xlsxSheet, 'Users');
        xlsxWriteFile(workBook, 'Export utilisateurs.xlsx');
    };

    return (
        <div className="cum-content-center">
            <div className="cum-header">
                <div className="cum-title">{t('Utilisateurs')}</div>
                <div className="cum-actions">
                    {/* Search */}
                    <div className="cum-search-wrapper" style={{ background: '#FAFAFA' }}>
                        <img
                            src="/Images/search.png"
                            alt="search"
                        />
                        <input
                            type="text"
                            id="cum-search"
                            onChange={(event) => setSearchValue(event.target.value)}
                            className="cum-search-input"
                            placeholder={t('Rechercher par nom ou email')}
                            style={{
                                background: '#FAFAFA',
                                backgroundColor: '#FAFAFA',
                                WebkitBoxShadow: '0 0 0px 1000px #FAFAFA inset',
                            }}
                        />
                    </div>

                    <button className="cum-btn-download" onClick={handleUsersExport}>
                        <img
                            src="/Images/download.png"
                            alt="download"
                        />
                        {t('Télécharger')}
                    </button>


                    {/* Inviter */}
                    <div className="dropdown">
                        <button
                            className="cum-btn-invite"
                            id="dropdownMenu2"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            {t('Inviter')}
                        </button>
                        <ul
                            className="dropdown-menu menu-list-2 menu-st"
                            aria-labelledby="dropdownMenu2"
                        >
                            {createUserMenuList.map((item, index) => (
                                <li
                                    key={index}
                                    className={index === 0 ? 'first-child' : index === 3 ? 'last-child' : ''}
                                >
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            setSelectedUserForCreateUpdate({ user: null, role: item.role });
                                            setShowCreateUpdate(true);
                                        }}
                                    >
                                        {item.text}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <Loader />
            ) : isError ? (
                <ErrorComponent />
            ) : (
                <div className="cum-box-wrapper">
                    <div>
                        <div>
                            <table className="cum-table">
                                <thead>
                                    <tr className="cum-thead-row">

                                        {columns.map(({ label, field, sortable }) => (
                                            <th
                                                className={`cum-thead-th ${field === 'delete' ? 'cum-thead-th-actions' : ''}`}

                                                onClick={
                                                    sortable
                                                        ? () =>
                                                            handleSortingChange(
                                                                field,
                                                                sortField,
                                                                sortOrder,
                                                                setSortField,
                                                                setSortOrder,
                                                                handleSorting
                                                            )
                                                        : null
                                                }
                                                key={field}
                                            >
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                                    {field === 'delete' ? (
                                                        <span style={{
                                                            borderLeft: '1px solid #0000001A',
                                                            paddingLeft: '8px',
                                                            height: '17px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                        }}>
                                                            {label}
                                                        </span>
                                                    ) : label}
                                                    {sortable && (
                                                        <img
                                                            src="/Images/chevron-up-down.png"
                                                            alt="sort"
                                                            style={{
                                                                width: 12,
                                                                height: 12,
                                                                opacity: field === sortField ? 1 : 0.3,
                                                                transform:
                                                                    field === sortField && sortOrder === 'desc'
                                                                        ? 'rotate(180deg)'
                                                                        : 'rotate(0deg)',
                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        />
                                                    )}

                                                    {field === 'role' ? (
                                                        <RoleTypeFilter
                                                            roleTypeList={filterRoleMenuList}
                                                            selectedType={selectedRole}
                                                            setSelectedType={setSelectedRole}
                                                            handleClick={handleRoleFilterClick}
                                                            handleClose={handleRoleFilterClose}
                                                            anchorEl={anchorElRole}
                                                        />
                                                    ) : null}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentUsers.map((user, index) => (
                                        <tr key={index} className="cum-tbody-row">
                                            <td
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setSelectedUserForCreateUpdate({
                                                        user: user,
                                                        role: user.role,
                                                    });
                                                    setShowCreateUpdate(true);
                                                }}
                                                className="cum-tbody-td"
                                            >
                                                <span className="cum-tbody-name">
                                                    {user.role === ROLES.ADMIN ||
                                                        user.role === ROLES.ADMIN_CLIENT ||
                                                        user.role === ROLES.GLOBAL_WRITER ||
                                                        user.role === ROLES.PROJECT_MANAGER
                                                        ? user.firstName + ' ' + user.lastName
                                                        : user.name}
                                                </span>
                                            </td>
                                            <td className="cum-tbody-td">
                                                <a href={`mailto:${user.mail}`} className="cum-tbody-email">{user.mail}</a>
                                            </td>
                                            <td className="cum-tbody-td">
                                                <span className="cum-tbody-status">{formatRole(user.role)}</span>
                                            </td>

                                            {/*<td>
                        <span>
                          {' '}
                          {user.role === ROLES.WRITER &&
                            user.analysis + '/' + user.limit}
                        </span>
                      </td>*/}


                                            <td className="cum-tbody-td">
                                                <div className="cum-actions-cell">

                                                    <span
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setSelectedUserForCreateUpdate({
                                                                user: user,
                                                                role: user.role,
                                                            });
                                                            setShowCreateUpdate(true);
                                                        }}
                                                        className="cum-action-btn"
                                                    >
                                                        <img src="/Images/modify.png" alt="edit" style={{ width: 24, height: 24 }} />
                                                    </span>
                                                    <span
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setSelectedUserForDelete({
                                                                mail: user.mail,
                                                                role: user.role,
                                                                firstName: user.firstName,
                                                                lastName: user.lastName,
                                                                name: user.name,
                                                            });
                                                            setShowDelete(true);
                                                        }}
                                                        className="cum-action-btn"
                                                        role="button"
                                                    >
                                                        <img src="/Images/icon-trash.png" alt="trash" style={{ width: 20, height: 20 }} />
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {filteredUsers.length > 0 && (
                <TablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disablePrev={currentPage === 1}
                    disableNext={currentPage === totalPages}
                />
            )}
            {showDelete && (
                <DeleteUserGlobal
                    selected={selectedUserForDelete}
                    Close={() => {
                        setShowDelete(false);
                    }}
                />
            )}
            {showDisable && (
                <DisableUser
                    selected={selectedUserForDisable}
                    Close={() => {
                        setShowDisable(false);
                    }}
                />
            )}

            {showCreateUpdate && (
                <CreateUpdateUser
                    selected={selectedUserForCreateUpdate}
                    Close={() => {
                        setShowCreateUpdate(false);
                        dispatch(fetchProjectManagerWriters());
                    }}
                />
            )}
        </div>
    );
}
